import {
  useCallback,
  useEffect,
  useRef,
  useState,
  type MouseEvent as ReactMouseEvent,
  type PointerEvent as ReactPointerEvent,
  type RefObject,
} from 'react'
import {
  FIT_VIEW_TRANSFORM,
  MAX_STRAIGHTEN_DEG,
  MIN_STRAIGHTEN_DEG,
  VIEW_ZOOM_MIN,
  integerRectInside,
  orientationCropBounds,
  panViewTransform,
  resizeRectKeepingRatio,
  zoomViewAroundPoint,
  type Handle,
  type Rect,
  type Size,
  type ViewTransform,
} from '@/core/geometry'
import { EditAction } from '@/core/history'
import { useEditSession, type EditSessionState } from '@/core/store/editSession'
import { selectEffectiveRatio, selectParentFrame, selectSelectedSource } from '@/core/store/selectors'
import { resetSelectedCrop, updateSelectedCrop } from '@/tools/crop/actions'
import {
  MIN_CROP_SHORT_SIDE,
  panFrame,
  zoomFrame,
  zoomFrameAroundPoint,
} from '@/tools/crop/gestures/frameMath'
import { isHandle } from '@/tools/crop/gestures/handle'
import { selectSelectedCrop } from '@/tools/crop/selectors'
import { recenterSelectedFreePlacement, updateSelectedFreePlacement } from '@/tools/frame/actions'
import { FrameStyleId, PhotoPlacement, type FreePlacement } from '@/tools/frame/params'
import { setSelectedStraighten } from '@/tools/orientation/actions'
import { selectSelectedOrientation } from '@/tools/orientation/selectors'

export enum FrameGestureMode {
  None = 'none',
  Crop = 'crop',
  FreePlacement = 'free-placement',
  Collage = 'collage',
}

enum SingleGestureMode {
  None = 'none',
  Crop = 'crop',
  FreePlacement = 'free-placement',
  View = 'view',
}

interface GestureOptions {
  viewport: Size
  compositionSize: Size
  fitTransform: ViewTransform
  editorRef: RefObject<HTMLElement | null>
  mode: FrameGestureMode
  photoWindowSize?: Size
  photoWindowCenter?: { x: number; y: number }
  placementWindowSize?: Size
  rotationDeg?: number
}

const WHEEL_COMMIT_DELAY_MS = 300
const DOUBLE_TAP_DELAY_MS = 300
const DOUBLE_TAP_VIEW_SCALE = 2.5

interface PointerSample {
  point: { x: number; y: number }
  pointerType: string
  isBackground: boolean
}

interface CropGestureStart {
  point: { x: number; y: number }
  rect: Rect
  screenScale: number
}

interface FreeGestureStart {
  point: { x: number; y: number }
  free: FreePlacement
}

function distance(first: PointerSample, second: PointerSample): number {
  return Math.hypot(first.point.x - second.point.x, first.point.y - second.point.y)
}

function midpoint(first: PointerSample, second: PointerSample): { x: number; y: number } {
  return {
    x: (first.point.x + second.point.x) / 2,
    y: (first.point.y + second.point.y) / 2,
  }
}

function angleDegrees(first: PointerSample, second: PointerSample): number {
  return Math.atan2(second.point.y - first.point.y, second.point.x - first.point.x) * 180 / Math.PI
}

function normalizedAngleDelta(previousDegrees: number, nextDegrees: number): number {
  const delta = (nextDegrees - previousDegrees + 540) % 360 - 180
  if (!Number.isFinite(delta)) throw new Error('Crop rotation gesture produced an invalid angle')
  return delta
}

function cropSession(state: EditSessionState) {
  const source = selectSelectedSource(state)
  const orientation = selectSelectedOrientation(state)
  return {
    crop: selectSelectedCrop(state),
    cropBounds: integerRectInside(orientationCropBounds(source, orientation)),
    ratio: selectEffectiveRatio(state),
  }
}

function freePlacementSession(state: EditSessionState, windowSize?: Size) {
  const node = selectParentFrame(state)
  if (!node) throw new Error('Free placement gestures require the active photo to have a parent frame')
  if (node.params.styleId !== FrameStyleId.Polaroid) {
    throw new Error(`Free placement gestures require a Polaroid frame, received "${node.params.styleId}"`)
  }
  if (node.params.placement !== PhotoPlacement.Free || !node.params.free) {
    throw new Error('Free placement gestures require a frame in Free mode')
  }
  if (!windowSize || windowSize.width <= 0 || windowSize.height <= 0) {
    throw new Error('Free placement gestures require a positive photo window size')
  }
  return { free: node.params.free, windowSize }
}

function localDelta(delta: { x: number; y: number }, rotationDeg: number): { x: number; y: number } {
  const radians = -rotationDeg * Math.PI / 180
  const cosine = Math.cos(radians)
  const sine = Math.sin(radians)
  return {
    x: delta.x * cosine - delta.y * sine,
    y: delta.x * sine + delta.y * cosine,
  }
}

function localRect(rect: Rect, bounds: Rect): Rect {
  return { ...rect, x: rect.x - bounds.x, y: rect.y - bounds.y }
}

function globalRect(rect: Rect, bounds: Rect): Rect {
  return { ...rect, x: rect.x + bounds.x, y: rect.y + bounds.y }
}

function eventElement(target: EventTarget | null): Element {
  if (!(target instanceof Element)) throw new Error('Canvas gesture target must be an element')
  return target
}

function isPhotoTarget(target: EventTarget | null): boolean {
  return eventElement(target).closest('[data-canvas-photo="true"]') !== null
}

function isActivePhotoTarget(target: EventTarget | null): boolean {
  return eventElement(target).closest('[data-canvas-photo-active="true"]') !== null
}

function isCanvasControlTarget(target: EventTarget | null): boolean {
  return eventElement(target).closest('[data-canvas-control="true"]') !== null
}

function handleForTarget(target: EventTarget | null): Handle | null {
  const value = (eventElement(target).closest('[data-handle]') as HTMLElement | null)?.dataset.handle
  if (value === undefined) return null
  if (!isHandle(value)) throw new Error(`Crop gesture received unknown handle "${value}"`)
  return value
}

export function useFrameGestures({
  viewport,
  compositionSize,
  fitTransform,
  editorRef,
  mode,
  photoWindowSize,
  photoWindowCenter,
  placementWindowSize,
  rotationDeg = 0,
}: GestureOptions) {
  const pointers = useRef(new Map<number, PointerSample>())
  const activeHandle = useRef<Handle | null>(null)
  const singleMode = useRef(SingleGestureMode.None)
  const cropGestureStart = useRef<CropGestureStart | null>(null)
  const freeGestureStart = useRef<FreeGestureStart | null>(null)
  const pinchActive = useRef(false)
  const cropPinchStraightenDeg = useRef<number | null>(null)
  const lastBackgroundTapAt = useRef(0)
  const moved = useRef(false)
  const spacePressed = useRef(false)
  const wheelCommitTimer = useRef<ReturnType<typeof setTimeout> | null>(null)
  const [isInteracting, setIsInteracting] = useState(false)

  const setViewZoom = useCallback((factor: number, clientPoint: { x: number; y: number }) => {
    const editor = editorRef.current
    if (!editor) throw new Error('View zoom requires the canvas editor element')
    const bounds = editor.getBoundingClientRect()
    const state = useEditSession.getState()
    state.setViewTransform(zoomViewAroundPoint(
      state.viewTransform,
      factor,
      { x: clientPoint.x - bounds.left, y: clientPoint.y - bounds.top },
      fitTransform,
      compositionSize,
      viewport,
    ))
  }, [compositionSize, editorRef, fitTransform, viewport])

  const panView = useCallback((delta: { x: number; y: number }) => {
    const state = useEditSession.getState()
    state.setViewTransform(panViewTransform(
      state.viewTransform,
      delta,
      fitTransform,
      compositionSize,
      viewport,
    ))
  }, [compositionSize, fitTransform, viewport])

  const toggleView = useCallback((clientPoint: { x: number; y: number }) => {
    const state = useEditSession.getState()
    if (state.viewTransform.scale > VIEW_ZOOM_MIN) {
      state.setViewTransform({ ...FIT_VIEW_TRANSFORM })
      return
    }
    setViewZoom(DOUBLE_TAP_VIEW_SCALE, clientPoint)
  }, [setViewZoom])

  const resetPhoto = useCallback(() => {
    if (mode === FrameGestureMode.FreePlacement) {
      recenterSelectedFreePlacement()
      return
    }
    if (mode === FrameGestureMode.Crop) resetSelectedCrop()
  }, [mode])

  const startPhotoGesture = useCallback((event: ReactPointerEvent<HTMLElement>): SingleGestureMode => {
    if (!isActivePhotoTarget(event.target)) return SingleGestureMode.None
    if (mode === FrameGestureMode.FreePlacement) {
      if (activeHandle.current) throw new Error('Free placement gestures do not support crop handles')
      const current = freePlacementSession(useEditSession.getState(), placementWindowSize)
      freeGestureStart.current = {
        point: { x: event.clientX, y: event.clientY },
        free: { ...current.free },
      }
      return SingleGestureMode.FreePlacement
    }
    if (mode !== FrameGestureMode.Crop) return SingleGestureMode.None
    const current = cropSession(useEditSession.getState())
    if (!photoWindowSize || photoWindowSize.width <= 0 || photoWindowSize.height <= 0) {
      throw new Error('Crop gestures require a positive rendered photo window size')
    }
    cropGestureStart.current = {
      point: { x: event.clientX, y: event.clientY },
      rect: { ...current.crop.rect },
      screenScale: photoWindowSize.width / current.crop.rect.width,
    }
    return SingleGestureMode.Crop
  }, [mode, photoWindowSize, placementWindowSize])

  const onPointerDown = useCallback((event: ReactPointerEvent<HTMLElement>) => {
    if (isCanvasControlTarget(event.target)) return
    const sample: PointerSample = {
      point: { x: event.clientX, y: event.clientY },
      pointerType: event.pointerType,
      isBackground: !isPhotoTarget(event.target),
    }
    pointers.current.set(event.pointerId, sample)
    if (pointers.current.size === 1) {
      moved.current = false
      activeHandle.current = handleForTarget(event.target)
      if (event.button === 1 || spacePressed.current) {
        event.preventDefault()
        singleMode.current = SingleGestureMode.View
      } else {
        singleMode.current = startPhotoGesture(event)
        if (singleMode.current === SingleGestureMode.None
          && sample.isBackground
          && useEditSession.getState().viewTransform.scale > VIEW_ZOOM_MIN) {
          singleMode.current = SingleGestureMode.View
        }
      }
    } else if (pointers.current.size === 2) {
      pinchActive.current = true
      activeHandle.current = null
      cropGestureStart.current = null
      freeGestureStart.current = null
      if (mode === FrameGestureMode.Crop) {
        cropPinchStraightenDeg.current = selectSelectedOrientation(useEditSession.getState()).straightenDeg
        singleMode.current = SingleGestureMode.None
      } else {
        singleMode.current = SingleGestureMode.View
      }
    }
    if (pointers.current.size === 2) {
      for (const pointerId of pointers.current.keys()) event.currentTarget.setPointerCapture(pointerId)
    } else if (singleMode.current !== SingleGestureMode.None) {
      event.currentTarget.setPointerCapture(event.pointerId)
    }
    if (singleMode.current !== SingleGestureMode.None || pointers.current.size === 2) setIsInteracting(true)
  }, [mode, startPhotoGesture])

  const onPointerMove = useCallback((event: ReactPointerEvent<HTMLElement>) => {
    const previous = pointers.current.get(event.pointerId)
    if (!previous) return
    const next: PointerSample = {
      ...previous,
      point: { x: event.clientX, y: event.clientY },
    }
    const delta = { x: next.point.x - previous.point.x, y: next.point.y - previous.point.y }
    if (Math.abs(delta.x) + Math.abs(delta.y) > 1) moved.current = true

    const pointerEntries = [...pointers.current.entries()].sort(([firstId], [secondId]) => firstId - secondId)
    if (pointerEntries.length === 2) {
      const firstEntry = pointerEntries[0]
      const secondEntry = pointerEntries[1]
      if (!firstEntry || !secondEntry) throw new Error('Pinch gesture state is missing a pointer')
      const previousFirst = firstEntry[1]
      const previousSecond = secondEntry[1]
      const nextFirst = firstEntry[0] === event.pointerId ? next : previousFirst
      const nextSecond = secondEntry[0] === event.pointerId ? next : previousSecond
      const previousDistance = distance(previousFirst, previousSecond)
      const previousMidpoint = midpoint(previousFirst, previousSecond)
      const nextMidpoint = midpoint(nextFirst, nextSecond)
      pointers.current.set(event.pointerId, next)
      if (previousDistance > 0) {
        const editor = editorRef.current
        if (!editor) throw new Error('Pinch gesture requires the canvas editor element')
        const editorBounds = editor.getBoundingClientRect()
        const factor = distance(nextFirst, nextSecond) / previousDistance
        if (mode === FrameGestureMode.Crop) {
          if (!photoWindowSize || photoWindowSize.width <= 0 || photoWindowSize.height <= 0) {
            throw new Error('Crop pinch requires a positive rendered photo window size')
          }
          if (!photoWindowCenter) throw new Error('Crop pinch requires the rendered photo window center')
          const straightenStart = cropPinchStraightenDeg.current
          if (straightenStart === null) throw new Error('Crop pinch rotation is missing its starting angle')
          const requestedStraighten = Math.min(MAX_STRAIGHTEN_DEG, Math.max(
            MIN_STRAIGHTEN_DEG,
            straightenStart + normalizedAngleDelta(
              angleDegrees(previousFirst, previousSecond),
              angleDegrees(nextFirst, nextSecond),
            ),
          ))
          cropPinchStraightenDeg.current = requestedStraighten
          setSelectedStraighten(requestedStraighten)

          const current = cropSession(useEditSession.getState())
          const screenScale = photoWindowSize.width / current.crop.rect.width
          const focalOffset = localDelta({
            x: previousMidpoint.x - editorBounds.left - photoWindowCenter.x,
            y: previousMidpoint.y - editorBounds.top - photoWindowCenter.y,
          }, rotationDeg)
          const zoomedCrop = Math.abs(factor - 1) > Number.EPSILON
            ? zoomFrameAroundPoint(
                current.crop.rect,
                factor,
                focalOffset,
                screenScale,
                current.cropBounds,
                current.ratio,
              )
            : current.crop.rect
          const midpointDelta = localDelta({
            x: nextMidpoint.x - previousMidpoint.x,
            y: nextMidpoint.y - previousMidpoint.y,
          }, rotationDeg)
          updateSelectedCrop(panFrame(
            zoomedCrop,
            midpointDelta,
            photoWindowSize.width / zoomedCrop.width,
            current.cropBounds,
          ))
          return
        }

        const state = useEditSession.getState()
        const zoomed = zoomViewAroundPoint(
          state.viewTransform,
          factor,
          { x: previousMidpoint.x - editorBounds.left, y: previousMidpoint.y - editorBounds.top },
          fitTransform,
          compositionSize,
          viewport,
        )
        state.setViewTransform(panViewTransform(
          zoomed,
          { x: nextMidpoint.x - previousMidpoint.x, y: nextMidpoint.y - previousMidpoint.y },
          fitTransform,
          compositionSize,
          viewport,
        ))
      }
      return
    }

    pointers.current.set(event.pointerId, next)
    if (pointers.current.size !== 1) return
    switch (singleMode.current) {
      case SingleGestureMode.View:
        panView(delta)
        return
      case SingleGestureMode.FreePlacement: {
        const current = freePlacementSession(useEditSession.getState(), placementWindowSize)
        const start = freeGestureStart.current
        if (!start) throw new Error('Free placement gesture requires a gesture start')
        const totalDelta = localDelta({
          x: next.point.x - start.point.x,
          y: next.point.y - start.point.y,
        }, rotationDeg)
        updateSelectedFreePlacement({
          ...start.free,
          offsetX: start.free.offsetX + totalDelta.x / current.windowSize.width,
          offsetY: start.free.offsetY + totalDelta.y / current.windowSize.height,
        })
        return
      }
      case SingleGestureMode.Crop: {
        const current = cropSession(useEditSession.getState())
        const start = cropGestureStart.current
        if (!start) throw new Error('Crop gesture requires a gesture start')
        const totalDelta = localDelta({
          x: next.point.x - start.point.x,
          y: next.point.y - start.point.y,
        }, rotationDeg)
        if (activeHandle.current) {
          const rect = resizeRectKeepingRatio(
            localRect(start.rect, current.cropBounds),
            activeHandle.current,
            { x: totalDelta.x / start.screenScale, y: totalDelta.y / start.screenScale },
            current.ratio,
            current.cropBounds,
            Math.min(MIN_CROP_SHORT_SIDE, current.cropBounds.width, current.cropBounds.height),
          )
          updateSelectedCrop(globalRect(rect, current.cropBounds))
          return
        }
        updateSelectedCrop(panFrame(start.rect, totalDelta, start.screenScale, current.cropBounds))
        return
      }
      case SingleGestureMode.None:
        return
      default:
        throw new Error(`Unknown single-pointer gesture mode: "${String(singleMode.current)}"`)
    }
  }, [compositionSize, editorRef, fitTransform, mode, panView, photoWindowCenter, photoWindowSize, placementWindowSize, rotationDeg, viewport])

  const finishPointer = useCallback((event: ReactPointerEvent<HTMLElement>) => {
    const sample = pointers.current.get(event.pointerId)
    const completedMode = singleMode.current
    const wasPinch = pinchActive.current
    pointers.current.delete(event.pointerId)
    if (pointers.current.size === 0) {
      if (moved.current) {
        if (wasPinch && mode === FrameGestureMode.Crop) {
          useEditSession.getState().commitHistory(EditAction.CropAndRotatePhoto)
        } else if (!wasPinch) {
          if (completedMode === SingleGestureMode.Crop) useEditSession.getState().commitHistory(EditAction.CropPhoto)
          if (completedMode === SingleGestureMode.FreePlacement) useEditSession.getState().commitHistory(EditAction.MovePrint)
        }
      }
      if (sample?.pointerType === 'touch' && sample.isBackground && !moved.current && !wasPinch) {
        const now = Date.now()
        if (now - lastBackgroundTapAt.current < DOUBLE_TAP_DELAY_MS) {
          toggleView(sample.point)
          lastBackgroundTapAt.current = 0
        } else {
          lastBackgroundTapAt.current = now
        }
      }
      activeHandle.current = null
      singleMode.current = SingleGestureMode.None
      cropGestureStart.current = null
      freeGestureStart.current = null
      pinchActive.current = false
      cropPinchStraightenDeg.current = null
      setIsInteracting(false)
    } else if (pointers.current.size === 1) {
      if (mode === FrameGestureMode.Crop) {
        const remaining = [...pointers.current.values()][0]
        if (!remaining) throw new Error('Crop gesture state is missing its remaining pointer')
        if (!photoWindowSize || photoWindowSize.width <= 0 || photoWindowSize.height <= 0) {
          throw new Error('Crop gesture continuation requires a positive rendered photo window size')
        }
        const current = cropSession(useEditSession.getState())
        cropGestureStart.current = {
          point: { ...remaining.point },
          rect: { ...current.crop.rect },
          screenScale: photoWindowSize.width / current.crop.rect.width,
        }
        singleMode.current = SingleGestureMode.Crop
      } else {
        singleMode.current = SingleGestureMode.View
      }
    }
  }, [mode, photoWindowSize, toggleView])

  const scheduleWheelCommit = useCallback((action: EditAction) => {
    if (wheelCommitTimer.current !== null) clearTimeout(wheelCommitTimer.current)
    wheelCommitTimer.current = setTimeout(() => {
      useEditSession.getState().commitHistory(action)
      wheelCommitTimer.current = null
    }, WHEEL_COMMIT_DELAY_MS)
  }, [])

  const onWheel = useCallback((event: WheelEvent) => {
    if (event.ctrlKey || event.metaKey) {
      event.preventDefault()
      setViewZoom(Math.exp(-event.deltaY * 0.002), { x: event.clientX, y: event.clientY })
      return
    }
    if (!isActivePhotoTarget(event.target)) return
    if (mode === FrameGestureMode.FreePlacement) {
      event.preventDefault()
      const current = freePlacementSession(useEditSession.getState(), placementWindowSize)
      updateSelectedFreePlacement({
        ...current.free,
        scale: current.free.scale * Math.exp(-event.deltaY * 0.002),
      })
      scheduleWheelCommit(EditAction.MovePrint)
      return
    }
    if (mode !== FrameGestureMode.Crop) return
    event.preventDefault()
    const current = cropSession(useEditSession.getState())
    updateSelectedCrop(zoomFrame(
      current.crop.rect,
      Math.exp(-event.deltaY * 0.002),
      current.cropBounds,
      current.ratio,
    ))
    scheduleWheelCommit(EditAction.CropPhoto)
  }, [mode, placementWindowSize, scheduleWheelCommit, setViewZoom])

  const onDoubleClick = useCallback((event: ReactMouseEvent<HTMLElement>) => {
    const hitTarget = document.elementFromPoint(event.clientX, event.clientY)
    if (!hitTarget) throw new Error('Canvas double-click requires a hit-test target')
    if (isPhotoTarget(hitTarget)) resetPhoto()
    else toggleView({ x: event.clientX, y: event.clientY })
  }, [resetPhoto, toggleView])

  useEffect(() => {
    const editor = editorRef.current
    if (!editor) throw new Error('Canvas gesture setup requires an editor element')
    editor.addEventListener('wheel', onWheel, { passive: false })
    return () => editor.removeEventListener('wheel', onWheel)
  }, [editorRef, onWheel])

  useEffect(() => {
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.code !== 'Space' || event.repeat) return
      const editor = editorRef.current
      const target = event.target
      const insideEditor = target instanceof Node && editor?.contains(target)
      if (target !== document.body && !insideEditor) return
      event.preventDefault()
      spacePressed.current = true
    }
    const onKeyUp = (event: KeyboardEvent) => {
      if (event.code === 'Space') spacePressed.current = false
    }
    window.addEventListener('keydown', onKeyDown)
    window.addEventListener('keyup', onKeyUp)
    return () => {
      window.removeEventListener('keydown', onKeyDown)
      window.removeEventListener('keyup', onKeyUp)
    }
  }, [editorRef])

  useEffect(() => () => {
    if (wheelCommitTimer.current !== null) clearTimeout(wheelCommitTimer.current)
    pointers.current.clear()
    activeHandle.current = null
    singleMode.current = SingleGestureMode.None
    cropGestureStart.current = null
    freeGestureStart.current = null
    pinchActive.current = false
    cropPinchStraightenDeg.current = null
  }, [])

  return {
    isInteracting,
    onPointerDown,
    onPointerMove,
    onPointerUp: finishPointer,
    onPointerCancel: finishPointer,
    onDoubleClick,
  }
}
